/**
 * Vocabulario de dominio do vinho.
 *
 * Estes valores viajam como string no banco (e nao como enum do Prisma) por dois
 * motivos: portabilidade entre MySQL/Postgres e tolerancia ao que a IA devolve,
 * que nem sempre cai exatamente num conjunto fechado.
 */
export const WINE_TYPES = [
    'tinto',
    'branco',
    'rose',
    'espumante',
    'fortificado',
    'sobremesa',
    'laranja',
    'outro',
];
export const WINE_TYPE_LABELS = {
    tinto: 'Tinto',
    branco: 'Branco',
    rose: 'Rosé',
    espumante: 'Espumante',
    fortificado: 'Fortificado',
    sobremesa: 'Sobremesa',
    laranja: 'Laranja',
    outro: 'Outro',
};
/**
 * Idade de safra a partir da qual uma garrafa SEM análise de guarda merece aviso.
 *
 * Não é urgência — sem janela o app continua não inventando uma. É um alerta de
 * que falta a análise justamente onde ela mais importa: a garrafa antiga sem
 * janela vai para o fim de "Beber antes" e pode passar do ponto sem ninguém ver.
 * Limiares conservadores por estilo (brancos e espumantes envelhecem mais cedo;
 * fortificados e de sobremesa aguentam décadas).
 */
const IDADE_DE_ATENCAO = {
    branco: 3,
    rose: 3,
    espumante: 3,
    laranja: 3,
    tinto: 6,
    outro: 5,
    sobremesa: 15,
    fortificado: 15,
};
const IDADE_DE_ATENCAO_SEM_TIPO = 5;
export function idadeSemAnaliseQuePedeAtencao(garrafa, anoAtual = new Date().getFullYear()) {
    const temJanela = garrafa.drinkFrom !== null || garrafa.drinkUntil !== null;
    if (temJanela || garrafa.vintage === null) {
        return null;
    }
    // Já analisada e sem janela: a IA respondeu que não dá para estimar. Insistir
    // em "vale analisar" mandaria pagar de novo pela mesma resposta — e o que
    // falta aparece no "porquê", que a análise gravou.
    if (garrafa.windowAnalyzedAt) {
        return null;
    }
    const idade = anoAtual - garrafa.vintage;
    const limite = garrafa.type ? IDADE_DE_ATENCAO[garrafa.type] : IDADE_DE_ATENCAO_SEM_TIPO;
    return idade >= limite ? idade : null;
}
/**
 * Perfil de consumo: se o vinho ganha ou não com o tempo na garrafa.
 *
 * Separado da janela de propósito. Todo vinho tem janela de CONSUMO ("beba até
 * 2027"), mas nem todo tem potencial de GUARDA — a maioria é feita para beber
 * jovem e só perde com os anos. Sem este campo a IA só tinha como responder em
 * termos de guarda, e inventava evolução para vinho que não evolui.
 *
 * Não substitui a janela: vinho jovem sem janela cairia em `sem_informacao` e
 * perderia o aviso de "Beber antes" — justamente o vinho que mais precisa dele.
 */
export const DRINK_PROFILES = ['beber_jovem', 'guarda_curta', 'guarda_longa'];
export const DRINK_PROFILE_LABELS = {
    beber_jovem: 'Para beber jovem',
    guarda_curta: 'Guarda curta',
    guarda_longa: 'Guarda longa',
};
export function isDrinkProfile(value) {
    return typeof value === 'string' && DRINK_PROFILES.includes(value);
}
/**
 * Como a adega controla a temperatura.
 *
 * Não é dado de cadastro: é entrada da análise. A IA estimava a janela
 * assumindo, em silêncio, que a garrafa estava bem guardada — e numa sala que
 * bate 30 °C no verão a janela real é bem mais curta.
 *
 * Um campo só, e não "é climatizada?" mais "qual tipo?": com dois campos existe
 * o estado inválido "não climatizada, com compressor".
 *
 * `null` (ausente) significa **não informado**, nunca "sem climatização".
 */
export const CLIMATIZACOES = ['nenhuma', 'compressor', 'termoeletrica'];
export const CLIMATIZACAO_LABELS = {
    nenhuma: 'Sem climatização',
    compressor: 'Climatizada · compressor',
    termoeletrica: 'Climatizada · termoelétrica',
};
/** O que cada opção significa para o vinho — mostrado na hora de escolher. */
export const CLIMATIZACAO_DESCRICOES = {
    nenhuma: 'Acompanha a temperatura da casa. A variação entre dia e noite pesa tanto quanto o calor.',
    compressor: 'Segura a temperatura mesmo com a casa quente. Vibra mais que a termoelétrica.',
    termoeletrica: 'Resfria alguns graus abaixo do ambiente: em dia muito quente pode não alcançar a temperatura de guarda.',
};
export function isClimatizacao(value) {
    return typeof value === 'string' && CLIMATIZACOES.includes(value);
}
export const BOTTLE_STATUSES = ['in_cellar', 'consumed'];
/**
 * Baldes de urgencia de consumo.
 *
 * `sem_informacao` existe de proposito: sem janela de guarda conhecida nao
 * inventamos uma estimativa por tipo de uva — a garrafa fica visivelmente
 * pendente de informacao ate o usuario preencher ou pedir a analise da IA.
 */
export const URGENCY_LEVELS = [
    'beber_agora',
    'beber_este_ano',
    'no_auge',
    'no_ponto',
    'guardar',
    'sem_informacao',
];
export const URGENCY_LABELS = {
    beber_agora: 'Beba agora',
    beber_este_ano: 'Beba este ano',
    no_auge: 'No auge',
    no_ponto: 'No ponto',
    guardar: 'Ainda guardando',
    sem_informacao: 'Sem informação de guarda',
};
/*
 * "no_auge" x "no_ponto": as duas estão dentro da janela, mas quem já chegou ao
 * ano de pico daqui para frente só perde — por isso vem antes na ordem. É a
 * distinção que responde "este vinho vai passar do momento ideal?".
 */
export const URGENCY_DESCRIPTIONS = {
    // "Consumo", não "guarda": vinho de beber jovem também passa da janela, e
    // nunca foi feito para guardar.
    beber_agora: 'Passou da janela ideal de consumo. Abra o quanto antes.',
    beber_este_ano: 'A janela ideal de consumo termina neste ano.',
    no_auge: 'Já chegou ao melhor momento. Daqui para frente tende a perder — priorize.',
    no_ponto: 'Dentro da janela e ainda evoluindo. Pode abrir, mas não tem pressa.',
    guardar: 'Ainda não entrou na janela ideal. Vale esperar.',
    sem_informacao: 'Sem janela de guarda registrada. Use a análise da adega, no topo de "Beber antes", para a IA estimar.',
};
export const DEFAULT_BOTTLE_VOLUME_ML = 750;
export function isWineType(value) {
    return typeof value === 'string' && WINE_TYPES.includes(value);
}
/**
 * Nome de exibição: produtor + nome ("Amayan Bonarda").
 *
 * No banco produtor e nome ficam separados (bom para filtrar e agrupar), e a
 * leitura de rótulo não repete o produtor dentro do nome. Mas exibir só o nome
 * deixava "Bonarda" — genérico, há centenas. É assim que se fala de vinho:
 * "Trivento Golden Reserve Malbec". Se o nome já contém o produtor, não duplica.
 */
export function nomeCompleto(garrafa) {
    const produtor = garrafa.producer?.trim();
    if (!produtor) {
        return garrafa.name;
    }
    const jaContem = garrafa.name.toLocaleLowerCase('pt-BR').includes(produtor.toLocaleLowerCase('pt-BR'));
    return jaContem ? garrafa.name : `${produtor} ${garrafa.name}`;
}
/**
 * Identidade do vinho: duas garrafas com a mesma chave são "iguais".
 *
 * Produtor + nome + safra, normalizados (caixa, espaços e acentos não contam).
 * É o que permite, sem ter uma tabela separada de "vinho", agrupar garrafas
 * iguais na tela e mandar só uma delas para a IA na análise da adega.
 */
export function chaveDoVinho(garrafa) {
    const normalizar = (texto) => (texto ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLocaleLowerCase('pt-BR')
        .replace(/\s+/g, ' ')
        .trim();
    return [normalizar(garrafa.producer), normalizar(garrafa.name), garrafa.vintage ?? 'sem-safra'].join('|');
}
/**
 * Rotulo legivel de uma posicao fisica, usado na UI e nas respostas da IA.
 *
 * O nome da adega so entra quando informado: com uma adega so ele seria ruido,
 * com varias ele e justamente o que diz em qual movel ir procurar a garrafa.
 */
export function formatPosition(shelfLabel, position, cellarName) {
    const local = `${shelfLabel}, posição ${position + 1}`;
    return cellarName ? `${cellarName} · ${local}` : local;
}
/**
 * Para que a adega serve. Muda o veredito da análise: guardar 50 garrafas para
 * 2030 é erro para quem bebe no dia a dia e é exatamente o plano para quem
 * guarda.
 */
export const FINALIDADES_DA_ADEGA = ['beber', 'guardar', 'ambos'];
export const FINALIDADE_LABELS = {
    beber: 'Beber no dia a dia',
    guardar: 'Guardar para ocasiões',
    ambos: 'As duas coisas',
};
export const FINALIDADE_DESCRICOES = {
    beber: 'Compro para abrir; o que fica guardado demais é desperdício.',
    guardar: 'Compro para envelhecer; abrir cedo é que seria desperdício.',
    ambos: 'Uma parte é para abrir agora, outra é para esperar.',
};
export function isFinalidadeDaAdega(value) {
    return typeof value === 'string' && FINALIDADES_DA_ADEGA.includes(value);
}
/**
 * Para que serve cada adega.
 *
 * Nasceu de uma correção do usuário: a adega de madeira dele, sem
 * climatização, não é um lugar ruim para guardar — é uma **passagem**, onde
 * ficam os vinhos que ele vai abrir em seguida. O dado confirmou: 71% das
 * garrafas de lá estão no auge ou depois dele, contra 42% e 25% nas outras.
 *
 * Sem isto, a análise repreende um uso que está certo: encurtar a janela de um
 * vinho que vai ser bebido em duas semanas não informa nada.
 */
export const PAPEIS_DA_ADEGA = ['guarda', 'passagem', 'mista'];
export const PAPEL_LABELS = {
    guarda: 'Guarda',
    passagem: 'Passagem rápida',
    mista: 'As duas coisas',
};
export const PAPEL_DESCRICOES = {
    guarda: 'Fica aqui o que vai esperar anos até a hora certa.',
    passagem: 'Onde ficam os vinhos que vou abrir nas próximas semanas.',
    mista: 'Parte espera, parte é para abrir logo.',
};
export function isPapelDaAdega(value) {
    return typeof value === 'string' && PAPEIS_DA_ADEGA.includes(value);
}
//# sourceMappingURL=wine.js.map